import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Alert, Button } from 'react-bootstrap';
import { GoogleLogin } from '@react-oauth/google';
import { PublicClientApplication } from '@azure/msal-browser';
import './AdminLogin.css';

const msalConfig = {
    auth: {
        clientId: "a6e3199e-1d66-4af5-b8a1-d40f7ce873cf", 
        authority: "https://login.microsoftonline.com/organizations", 
        redirectUri: window.location.origin + "/admin-portal-access",
    },
    cache: {
        cacheLocation: "localStorage", 
        storeAuthStateInCookie: false, 
    }
};

const msalInstance = new PublicClientApplication(msalConfig);

const AdminLogin = ({ onLogin }) => {
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const [isMsLoading, setIsMsLoading] = useState(false);
    const [isMsalReady, setIsMsalReady] = useState(false);

    useEffect(() => {
        const initMsal = async () => {
            try {
                await msalInstance.initialize();
                const loginResponse = await msalInstance.handleRedirectPromise();
                setIsMsalReady(true);
                if (loginResponse) {
                    setIsMsLoading(true);
                    await handleBackendVerification(loginResponse.accessToken);
                }
                
            } catch (e) {
                console.error("MSAL Init Error:", e);
                if (e.innerHTML && e.innerHTML.includes("no_token_request_cache_error")) {
                    setIsMsalReady(true);
                } else {
                    setError("Failed to initialize Microsoft login system.");
                }
            } finally {
                setIsMsLoading(false);
            }
        };
        initMsal();
    }, []);

    const handleBackendVerification = async (accessToken) => {
        try {
            //const response = await fetch('http://localhost:5000/api-portal/microsoft-login', {
            const response = await fetch('/api-portal/microsoft-login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token: accessToken }),
            });

            const data = await response.json();

            if (data.success) {
                localStorage.setItem('adminToken', data.token);
                onLogin(true);
                navigate('/admin-dashboard'); 
            } else {
                setError(data.message || 'Access denied. Microsoft account not authorized.');
            }
        } catch (err) {
            console.error("Backend Error:", err);
            setError('Server connection failed. Make sure your backend is running.');
        }
    };

    // --- GOOGLE LOGIN ---
    const handleGoogleSuccess = async (credentialResponse) => {
        setError('');
        try {
            //const response = await fetch('http://localhost:5000/api-portal/google-login', {
            const response = await fetch('/api-portal/google-login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token: credentialResponse.credential }),
            });

            const data = await response.json();

            if (data.success) {
                localStorage.setItem('adminToken', data.token);
                onLogin(true);
                navigate('/admin-dashboard');
            } else {
                setError(data.message || 'Access denied. Authorized personnel only.');
            }
        } catch (err) {
            setError('Server connection failed. Make sure your backend is running.');
        }
    };

    // --- MICROSOFT LOGIN ---
    const handleMicrosoftLogin = async () => {
        if (!isMsalReady) return;

        setError('');
        setIsMsLoading(true);
        try {
            await msalInstance.loginRedirect({
                scopes: ["User.Read"],
                prompt: "select_account" 
            });
        } catch (err) {
            console.error("Microsoft Redirect Error:", err);
            setError('Failed to redirect to Microsoft.');
            setIsMsLoading(false);
        }
    };

    return (
        <div className="admin-login-page">
            <Container className="d-flex justify-content-center align-items-center h-100">
                <div className="login-card text-center animate-in">
                    
                    <div className="mb-4">
                        <h2 className="login-title">INFINITI <span className="blue-text">ADMIN</span></h2>
                        <p className="text-muted small">Management & Operations Portal</p>
                    </div>

                    {error && <Alert variant="danger" className="py-2 small">{error}</Alert>}

                    {/* Google Login */}
                    <div className="d-flex justify-content-center mt-4">
                        <GoogleLogin
                            onSuccess={handleGoogleSuccess}
                            onError={() => setError('Google Login Failed')}
                            theme="filled_blue"
                            shape="pill"
                            text="signin_with"
                            width="250"
                        />
                    </div>

                    <div className="text-muted my-3 small">─ OR ─</div>

                    {/* Microsoft Login */}
                    <div className="d-flex justify-content-center">
                        <Button 
                            onClick={handleMicrosoftLogin}
                            disabled={isMsLoading || !isMsalReady}
                            className="w-100 d-flex align-items-center justify-content-center gap-2"
                            style={{ backgroundColor: '#2f2f2f', border: 'none', borderRadius: '50px', padding: '8px 24px', maxWidth: '250px', fontSize: '14px', fontWeight: '500' }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 23 23" fill="none" className="me-1">
                                <path d="M0 0H11V11H0V0Z" fill="#F25022"/>
                                <path d="M12 0H23V11H12V0Z" fill="#7FBA00"/>
                                <path d="M0 12H11V23H0V12Z" fill="#00A1F1"/>
                                <path d="M12 12H23V23H12V12Z" fill="#FFB900"/>
                            </svg>
                            {!isMsalReady ? 'Initializing...' : isMsLoading ? 'Connecting...' : 'Sign in with Microsoft'}
                        </Button>
                    </div>

                    <div className="mt-5 access-footer">
                        <p className="mb-1 text-uppercase fw-bold text-secondary" style={{ fontSize: '10px', letterSpacing: '2px' }}>
                            Authorized Personnel Only
                        </p>
                    </div>
                </div>
            </Container>
        </div>
    );
};

export default AdminLogin;